import { SolutionPage } from "@/components/shared/SolutionPage";
import { DatabaseReactivationMockup } from "@/components/shared/DatabaseReactivationMockup";
import { RefreshCw, Calendar, Network, TrendingUp } from 'lucide-react';
import { Link } from "react-router-dom";
import { ServiceFAQ } from "@/components/shared/ServiceFAQ";

/**
 * Capability spoke - "Database Reactivation".
 * Single-intent page for "database reactivation" / "reactivate old leads" /
 * "dead deal reactivation" demand. Hub is /platform; clustered to its parent
 * layers /solutions/crm-automation (the database) + /solutions/lead-generation-system
 * (works revived deals to a booking). Outcome-framed, no invented metrics.
 * CASL-safe: re-engages the client's OWN contacts (existing business relationship),
 * opt-out on every message - not cold outreach.
 */
const DatabaseReactivation = () => {
  return (
    <SolutionPage
      breadcrumbTitle="Database Reactivation"
      seo={{
        title: "Database Reactivation Campaigns: Old Leads Into Sales | Frayze",
        description: "Frayze mines the leads and past customers already in your CRM, re-engages them with a smart campaign, and books the ones ready to buy: new revenue from a database you already paid for, no new ad spend.",
        keywords: "database reactivation, dead deal reactivation, reactivate old leads, customer win-back, database reactivation campaign, re-engage old leads, crm reactivation",
        canonical: "https://frayze.ca/platform/database-reactivation/"
      }}
      hero={{
        badge: "Capability: Retention",
        badgeColor: "text-emerald-700",
        title: "Turn the Dead Leads in Your CRM Into Booked Sales.",
        description: "You've paid to collect thousands of leads and past customers who went quiet. Frayze re-engages them with a smart, personal campaign and books the ones ready to buy: new revenue from a database you already own, no new ad spend.",
        buttonColor: "bg-emerald-700 hover:bg-emerald-800",
        gradientParams: "from-emerald-500/5 via-background to-background",
        primaryCta: { text: "Reactivate My Database", link: "/book-consultation/?tier=foundation" },
        secondaryCta: { text: "See Pricing", link: "/pricing/" },
        visual: <DatabaseReactivationMockup />
      }}
      problem={{
        title: "Your Next Sale Is Probably Already in Your CRM",
        description: (
          <>
            Every quote that never closed, every lead that went cold, every past customer who drifted: they're sitting in your database right now, and you already paid to acquire them. Most businesses let them rot.
            <br /><br />
            <strong>The math is hard to argue with:</strong>
          </>
        ),
        gridCols: 3,
        cards: [
          { value: "5 to 25×", label: "More expensive to acquire a new customer than to keep one you already have (Harvard Business Review, 2014)" },
          { value: "$0", label: "Extra ad spend to re-engage a lead already sitting in your CRM" },
          { value: "Idle", label: "Every dormant contact is money you already spent: doing nothing until it's worked" }
        ]
      }}
      systemModel={{
        title: "How Database Reactivation Works",
        description: "It mines the database you already have and wakes it up, without you lifting a finger.",
        variant: "steps",
        connectionColor: "bg-gradient-to-r from-emerald-500/20 via-emerald-500/50 to-emerald-500/20",
        items: [
          { step: "1", title: "Find the Dormant Value", description: "The system segments your CRM, old quotes, cold leads, past customers, into who is actually worth re-engaging." },
          { step: "2", title: "Personal Re-Engagement", description: "Each segment gets a relevant, human-sounding text and email, not a blast, timed and sequenced to earn replies." },
          { step: "3", title: "Conversations, Not Spam", description: "Replies land in one inbox. Your team or the AI answers, qualifies, and books the ones with real intent." },
          { step: "4", title: "Booked & Tracked", description: "Revived leads flow into your pipeline with the full history attached, so nothing gets double-worked or dropped." }
        ]
      }}
      layers={{
        title: "What Comes With Database Reactivation",
        description: "A campaign engine that turns a static list into booked appointments: built, segmented, and run for you.",
        theme: "teal",
        items: [
          {
            title: "The Reactivation Engine",
            icon: RefreshCw,
            items: [
              "Smart segmentation of your CRM",
              "Personalized multi-touch SMS + email",
              "Timed sequences, not one-off blasts"
            ]
          },
          {
            title: "Then It Books Them",
            icon: Calendar,
            items: [
              "Replies handled in one unified inbox",
              "AI can qualify and book automatically",
              "Revived leads routed into your pipeline"
            ]
          }
        ]
      }}
      interdependence={{
        title: "Reactivation Runs on the System You Already Have",
        description: (
          <>
            Reactivation only works if the data is clean and the follow-up is automated. It mines the <Link to="/solutions/crm-automation/" className="font-bold underline decoration-teal-500/50 hover:text-teal-600 transition-colors">CRM that stores every lead and customer</Link>, feeds revived deals into the <Link to="/solutions/lead-generation-system/" className="font-bold underline decoration-indigo-500/50 hover:text-indigo-600 transition-colors">lead system that works them to a booking</Link>, and can hand the conversation to <Link to="/platform/" className="font-bold underline decoration-purple-500/50 hover:text-purple-600 transition-colors">the AI</Link> to qualify and book.
            <br /><br />
            See where it sits in the wider <Link to="/platform/" className="font-bold underline decoration-teal-500/50 hover:text-teal-600 transition-colors">managed platform</Link>.
          </>
        ),
        icon: Network,
        quote: "The cheapest lead you'll ever get is the one you already paid for."
      }}
      useCases={{
        title: "Where Database Reactivation Pays Off Most",
        items: [
          { title: "Car Dealerships", description: "Years of un-closed finance apps, service customers due for an upgrade, and cold internet leads: re-engaged into showroom appointments.", link: "/automotive/" },
          { title: "Trades & Contractors", description: "Every quote you sent that went quiet is a follow-up waiting to happen: the system does it for you.", link: "/industries/trades/" },
          { title: "Any Business Sitting on a List", description: "If you have a CRM, a spreadsheet, or a pile of old leads, there's revenue in it you haven't worked.", link: "/solutions/crm-automation/" }
        ]
      }}
      socialProof={{
        title: "The operator playbook that worked dead deals at scale",
        quote: "Before Frayze, Denis built the follow-up systems that kept six dealership rooftops working every old lead and past customer, not just the fresh ones. That dead-deal reactivation discipline is what Frayze installs for you.",
        icon: TrendingUp,
        ctaText: "See the Need A Car story",
        ctaLink: "/case-studies/need-a-car-automotive-growth/"
      }}
      cta={{
        title: "There's Revenue Sitting in Your Database Right Now",
        description: "Put your old leads and past customers back to work and book the ones ready to buy, no new ad spend.",
        primaryCta: { text: "Reactivate My Database", link: "/book-consultation/?tier=foundation" },
        secondaryCta: { text: "Get Your Free Database Audit", link: "/digital-audit/" },
        color: "bg-emerald-700 hover:bg-emerald-800"
      }}
      customSection={
        <div className="space-y-24 pb-24">
          <ServiceFAQ
            title="Database Reactivation: Common Questions"
            description="Straight answers on how reactivation mines your CRM and turns old leads into booked sales."
            items={[
              {
                question: "What is database reactivation?",
                answer: "Database reactivation is a campaign that re-engages the leads and past customers already in your CRM, old quotes, cold leads, and buyers who went quiet, with personal, well-timed messages that restart the conversation and book the ones ready to act. It is the cheapest source of new revenue because you already paid to acquire those contacts."
              },
              {
                question: "Isn't messaging old contacts just spam? Is it CASL-compliant?",
                answer: "Done right it is the opposite of spam, and it stays onside with Canada's anti-spam law (CASL). An existing business relationship gives implied consent for up to two years after a purchase, contract or transaction, and up to six months after an inquiry. Contacts outside those windows need express consent, so we segment the list by last-contact date before anything is sent. Every message identifies your business and carries a working opt-out. This is general information, not legal advice."
              },
              {
                question: "How fast will I see results?",
                answer: "Reactivation is usually the fastest return of anything we turn on, because the audience already knows you: the first booked appointments often come inside the first campaign. We won't promise a specific number, but the leads are already yours; we are simply working them."
              },
              {
                question: "What do you need from me to run it?",
                answer: "Access to your existing contacts: a CRM export, a spreadsheet, or your current system. Frayze cleans and segments the list, writes the campaign, and runs it. If your data is messy, that is normal; getting it usable is part of the setup."
              },
              {
                question: "Will it annoy my existing customers?",
                answer: "That is exactly what the segmentation prevents. People are messaged based on where they left off and how they last engaged, at a sensible cadence, with an easy way to opt out. The goal is to feel like a timely, relevant nudge, not a mass text."
              },
              {
                question: "How much does database reactivation cost?",
                answer: "Database reactivation campaigns are included in the Frayze Growth plan at $699/month, together with AI website chat, email and SMS campaign management, and call tracking. Setup, including cleaning and segmenting your list, is scoped on a call with a fixed quote before you commit. Text and email usage is billed like a phone plan. No long-term contract."
              },
              {
                question: "Do you run reactivation campaigns outside Thunder Bay?",
                answer: "Yes. Setup and campaigns run remotely, so Frayze runs reactivation for businesses across Canada and the US."
              },
              {
                question: "What happens when someone replies?",
                answer: "Replies land in one inbox. Your team can answer, or Frayze Conversation AI can qualify the lead and book the appointment automatically, then drop it into your pipeline with the full history so nothing gets worked twice."
              }
            ]}
          />
        </div>
      }
    />
  );
};

export default DatabaseReactivation;
